import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import getBrandTileReviews from "@faire/web-api--source/api/v2/products/search/brand-tile-reviews/post";
import { getQueryKey_escape_hatch as getQueryKeyBrandTileReviews } from "@faire/web-api--source/api/v2/products/search/brand-tile-reviews/post-hooks";
import { IBrandTileBrandReviewsRequest } from "@faire/web-api--source/indigofair/data/IBrandTileBrandReviewsRequest";
import { IBrandTileBrandReviewsResponse } from "@faire/web-api--source/indigofair/data/IBrandTileBrandReviewsResponse";
import { useQuery } from "@tanstack/react-query";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";

import { ILeanBrandTile } from "@faire/retailer/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useLoggedOutCategoryPageCatNavResponse } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageCatNav/useLoggedOutCategoryPageCatNavResponse";

const STALE_TIME = 1000 * 60 * 5;
export const useLoggedOutCategoryPageBrandTileReviewsResponse = () => {
  const category = useLoggedOutCategoryPageCurrentCategory();
  const shouldGetBrandReviews = !!category.pathIdentifier;
  const productFirstSearchResults =
    useLoggedOutCategoryPageCatNavResponse().data?.productFirstSearchResults;
  const brandTokens = uniq(
    productFirstSearchResults
      ?.map((p) => p.brand?.token)
      .filter(isNotUndefined) ?? []
  );

  const request = IBrandTileBrandReviewsRequest.build({
    brand_tokens: brandTokens,
    query: category.mostSpecificName.toLowerCase(),
  });

  const { data } = useQuery({
    queryKey: getQueryKeyBrandTileReviews(request),
    queryFn: () => getBrandTileReviews(request),
    enabled: shouldGetBrandReviews && !!productFirstSearchResults?.length,
    staleTime: STALE_TIME,
  });

  return useMemo(() => {
    const brandReviews = data?.brand_tile_reviews ?? [];
    if (!brandReviews?.length || !productFirstSearchResults) {
      return [];
    }

    const brands = uniqBy(
      productFirstSearchResults.map((p) => p.brand),
      "token"
    );
    const brandTiles = brands
      .map((brand) => {
        return ILeanBrandTile.fromLeanProductTileBrand(brand);
      })
      .filter(isNotUndefined);

    return (
      brandTiles
        .map((brandTile) => ({
          ...brandTile,
          review: brandTile.brand?.token
            ? findReviewForBrandTile(brandTile, brandReviews)
            : undefined,
        }))
        .filter(
          (item) =>
            item.review !== undefined &&
            item.review.average_rating !== undefined &&
            item.review.total_reviews !== undefined &&
            item.review.review_comment !== undefined
        ) ?? []
    );
  }, [data?.brand_tile_reviews, productFirstSearchResults]);
};

const findReviewForBrandTile = (
  brandTile: ILeanBrandTile,
  brandReviews: Array<IBrandTileBrandReviewsResponse.IBrandTileBrandReview>
) => {
  return brandReviews?.find((review) => {
    if (brandTile.brand === undefined || brandTile.brand.token === undefined) {
      return false;
    }
    return brandTile.brand.token === review.brand_token;
  });
};
